import * as THREE from 'three';
import {SketchCollectionObject} from '../sketch/SketchCollectionObject';
import {SketchCircleObject} from '../sketch/SketchCircleObject';
import {SketchEllipseObject} from '../sketch/SketchEllipseObject';
import {SketchSlotObject} from '../sketch/SketchSlotObject';
import {SketchSplineObject} from '../sketch/SketchSplineObject';
import {SketchStyleSplineObject} from '../sketch/SketchStyleSplineObject';
import type {CollectSource} from '../sketch/ISketchObject';
import {isSketchCollectionObject} from '../guards/isSketchCollectionObject';
import {isSketchArcObject} from '../guards/isSketchArcObject';
import {isSketchCircleObject} from '../guards/isSketchCircleObject';
import {isSketchEllipseObject} from '../guards/isSketchEllipseObject';
import {isSketchLineObject} from '../guards/isSketchLineObject';
import {isSketchParabolaObject} from '../guards/isSketchParabolaObject';
import {isSketchSlotObject} from '../guards/isSketchSlotObject';
import {isSketchSplineObject} from '../guards/isSketchSplineObject';
import {isSketchStyleSplineObject} from '../guards/isSketchStyleSplineObject';
import {SketchClosureResolver} from '../../sketch/closure';
import {SketchContourGeometryUpdater} from './SketchContourGeometryUpdater';

/**
 * {@link SketchContour} 构造参数。
 */
export interface SketchContourOptions {
    /** 轮廓来源对象。 */
    source: SketchCollectionObject | SketchEllipseObject | SketchCircleObject | SketchSlotObject | SketchSplineObject | SketchStyleSplineObject;
}

/**
 * 判断对象是否为草图轮廓对象。
 * @param value 待判断对象。
 * @returns 当对象为草图轮廓对象时返回 true。
 */
export function isSketchContour(value: THREE.Object3D): value is SketchContour {
    return Reflect.get(value, 'isSketchContour') === true;
}

/**
 * 草图轮廓对象。
 * 职责：
 * 1. 直接作为 `THREE.Mesh` 参与渲染；
 * 2. 通过 `THREE.Shape + THREE.ShapeGeometry` 生成轮廓面；
 * 3. 维护轮廓基础元数据（来源、顶点快照）。
 */
export class SketchContour extends THREE.Mesh {
    /** 运行时类型标记。 */
    public readonly isSketchContour: boolean = true;

    source: SketchCollectionObject | SketchEllipseObject | SketchCircleObject | SketchSlotObject | SketchSplineObject | SketchStyleSplineObject;

    /** 负责复用或安全替换轮廓 Geometry 的更新器。 */
    private readonly geometryUpdater: SketchContourGeometryUpdater;

    /**
     * 输出轮廓相关错误日志。
     * @param message 错误信息。
     */
    private static logContourError(message: string): void {
        console.error(`[SketchContour] ${message}`);
    }

    /**
     * 构造草图轮廓对象。
     * @param options 构造参数。
     */
    public constructor(options: SketchContourOptions) {
        const points = SketchContour.collectPointsFromSource(options.source);
        const geometryUpdater = new SketchContourGeometryUpdater(options.source.referencePlane);
        const geometry = geometryUpdater.createGeometry(points);
        if (!geometry) {
            SketchContour.logContourError('轮廓点无法构建有效几何，已回退为空几何。');
        }
        const material = new THREE.MeshBasicMaterial({
            color: "#afafd6",
            transparent: true,
            opacity: 0.8,
            side: THREE.DoubleSide,
            depthWrite: false,
        });
        super(geometry ?? new THREE.BufferGeometry(), material);
        this.source = options.source;
        this.geometryUpdater = geometryUpdater;
    }

    /**
     * 以新顶点集更新轮廓几何。
     * 说明：
     * 1. 该方法用于拖拽过程中轮廓实时刷新；
     * 2. 顶点与索引布局不变时原位更新既有 Geometry 缓冲；
     * 3. 拓扑布局变化时安全替换 Geometry 并释放旧资源。
     * @param points 新的轮廓顶点数组（世界坐标）。
     */
    public updatePoints(points: THREE.Vector3[]): void {
        if (!Array.isArray(points) || points.length < 3) {
            return;
        }
        const nextGeometry = this.geometryUpdater.updateGeometry(this.geometry, points);

        if (!nextGeometry) {
            return;
        }

        if (nextGeometry !== this.geometry) {
            const previousGeometry = this.geometry;
            this.geometry = nextGeometry;
            previousGeometry.dispose();
        }
    }

    /**
     * 基于当前来源对象重新同步轮廓几何。
     * 说明：
     * 1. 该方法用于来源对象几何已变化、但轮廓对象实例需要复用的场景；
     * 2. 内部会重新采样来源对象顶点并更新当前轮廓；
     * 3. 采样结果无效时直接抛错，不做兜底处理。
     */
    public syncSource(): void {
        const points = SketchContour.collectPointsFromSource(this.source);
        this.updatePoints(points);
    }

    //------ 轮廓顶点访问 开始------
    /**
     * 获取当前轮廓的顶点列表。
     * 说明：
     * 1. 顶点始终基于当前来源对象实时采样；
     * 2. 返回值仅作为调用方计算使用，不复用外部传入对象；
     * 3. 若来源对象无法提供合法顶点，则直接抛错。
     * @returns 当前轮廓顶点数组。
     */
    public getContourPoints(): THREE.Vector3[] {
        return SketchContour.collectPointsFromSource(this.source);
    }
    //------ 轮廓顶点访问 结束------

    /**
     * 获取当前轮廓关联的草图对象列表。
     * 说明：
     * 1. 轮廓自身负责解析来源对象与其关联边；
     * 2. 调用方无需关心来源对象的具体类型；
     * 3. 若当前来源没有可联动对象，则返回空数组。
     * @returns 关联草图对象列表。
     */
    public getLinkObjects(): THREE.Object3D[] {
        if (isSketchCollectionObject(this.source)) {
            return Array.from(this.source.collectSet);
        }

        if (isSketchSlotObject(this.source)) {
            return [...this.source.getEdges()];
        }

        if (
            isSketchCircleObject(this.source)
            || isSketchEllipseObject(this.source)
            || isSketchSplineObject(this.source)
            || isSketchStyleSplineObject(this.source)
        ) {
            return [this.source];
        }

        return [];
    }

    /**
     * 从轮廓来源对象收集轮廓点。
     * @param source 轮廓来源对象。
     * @returns 轮廓点数组。
     */
    private static collectPointsFromSource(
        source: SketchCollectionObject | SketchEllipseObject | SketchCircleObject | SketchSlotObject | SketchSplineObject | SketchStyleSplineObject
    ): THREE.Vector3[] {
        if (isSketchCollectionObject(source)) {
            const sources = Array.from(source.collectSet);
            if (!Array.isArray(sources) || sources.length < 1) {
                SketchContour.logContourError('集合源对象数量不足，无法构建轮廓顶点。');
                return [];
            }
            return SketchContour.collectClosedEdgeContourPoints(sources, '集合源对象');
        }

        if (isSketchSlotObject(source)) {
            // 槽口的共享 SketchPointObject 接点才是拓扑事实，BufferGeometry 仅用于显示且可能尚未同步。
            return SketchContour.collectClosedEdgeContourPoints(source.getEdges(), '槽口');
        }

        const position = source.geometry.getAttribute('position');
        if (!position || position.count < 3) {
            SketchContour.logContourError('轮廓来源几何顶点数量不足，无法构建轮廓。');
            return [];
        }

        const points: THREE.Vector3[] = [];
        for (let index = 0; index < position.count; index += 1) {
            points.push(new THREE.Vector3(
                position.getX(index),
                position.getY(index),
                position.getZ(index),
            ));
        }
        return points;
    }

    /**
     * 按共享接点拓扑采样有序闭合边链。
     *
     * 设计说明：
     * 1. `SketchClosureResolver` 统一解析边顺序与方向，不使用固定距离猜测接头；
     * 2. 每条边再按数学定义采样，渲染 BufferGeometry 的顶点顺序不参与拓扑判断；
     * 3. 每段丢弃末端重复接点，返回首尾不重复但拓扑闭合的面点环。
     *
     * @param sources 待解析的草图边。
     * @param sourceLabel 错误日志中的来源标签。
     * @returns 有序闭合点环；拓扑或采样无效时返回空数组。
     */
    private static collectClosedEdgeContourPoints(
        sources: readonly CollectSource[],
        sourceLabel: '集合源对象' | '槽口',
    ): THREE.Vector3[] {
        const sourceList = [...sources];
        const firstSource = sourceList[0];
        const traversalSteps = firstSource
            ? SketchClosureResolver.resolveClosedGeometryTraversal([firstSource], sourceList)
            : null;
        if (!traversalSteps || traversalSteps.length !== sourceList.length) {
            SketchContour.logContourError(`${sourceLabel}无法解析闭合遍历链，无法构建轮廓顶点。`);
            return [];
        }

        const points: THREE.Vector3[] = [];
        for (const traversalStep of traversalSteps) {
            const sourcePoints = SketchContour.collectEdgeContourPoints(traversalStep.source);
            if (sourcePoints.length < 2) {
                SketchContour.logContourError(`${sourceLabel}几何采样点数量不足，无法构建轮廓。`);
                return [];
            }
            const orderedSourcePoints = traversalStep.reversed
                ? sourcePoints.slice().reverse()
                : sourcePoints;
            orderedSourcePoints.slice(0, -1).forEach((point) => {
                points.push(point.clone());
            });
        }

        if (points.length < 3) {
            SketchContour.logContourError(`${sourceLabel}采样后的轮廓顶点数量不足。`);
            return [];
        }
        return points;
    }

    /**
     * 按边对象语义采集轮廓采样点。
     * 说明：
     * 1. 集合轮廓不再直接依赖每类边对象当前 geometry 的内部实现；
     * 2. 线段使用首尾端点，圆弧与部分椭圆使用曲线定义重新采样，样条直接复用曲线采样点；
     * 3. 这样可避免混合边在局部几何重建时出现 `position` 属性尚未稳定的瞬时失败。
     * @param source 集合边对象。
     * @returns 当前边对象的有序采样点列表。
     */
    private static collectEdgeContourPoints(
        source: CollectSource,
    ): THREE.Vector3[] {
        if (isSketchLineObject(source)) {
            return [
                source.startPoint.getPosition().clone(),
                source.endPoint.getPosition().clone(),
            ];
        }

        if (isSketchArcObject(source)) {
            const definition = source.getArcDefinition();
            const referencePlane = source.referencePlane;
            const localCenter = referencePlane.worldToLocal(definition.centerWorld);
            const curve = new THREE.EllipseCurve(
                localCenter.x,
                localCenter.y,
                definition.radius,
                definition.radius,
                definition.startAngle,
                definition.endAngle,
                definition.clockwise,
                0,
            );
            return curve.getPoints(definition.segments).map((point) => (
                referencePlane.localToWorld(new THREE.Vector2(point.x, point.y)).clone()
            ));
        }

        if (isSketchEllipseObject(source)) {
            // 椭圆弧按椭圆定义重新采样，与 `SketchEllipseObject.updateGeometry()` 同口径，
            // 避免依赖尚未同步的渲染 BufferGeometry 顶点顺序。
            const definition = source.getEllipseDefinition();
            const referencePlane = source.referencePlane;
            const localCenter = referencePlane.worldToLocal(definition.centerWorld);
            const curve = new THREE.EllipseCurve(
                localCenter.x,
                localCenter.y,
                definition.radiusX,
                definition.radiusY,
                definition.startAngle,
                definition.endAngle,
                definition.clockwise,
                definition.rotation,
            );
            return curve.getPoints(definition.segments).map((point) => (
                referencePlane.localToWorld(new THREE.Vector2(point.x, point.y)).clone()
            ));
        }

        if (isSketchParabolaObject(source)) {
            const position = source.geometry.getAttribute('position');
            if (!position || position.count < 2) {
                SketchContour.logContourError('抛物线几何顶点数量不足，无法构建集合轮廓。');
                return [];
            }

            const points: THREE.Vector3[] = [];
            for (let index = 0; index < position.count; index += 1) {
                points.push(new THREE.Vector3(
                    position.getX(index),
                    position.getY(index),
                    position.getZ(index),
                ));
            }
            return points;
        }

        if (isSketchSplineObject(source) || isSketchStyleSplineObject(source)) {
            return source.getCurvePoints();
        }

        SketchContour.logContourError('当前集合源对象类型不支持轮廓采样。');
        return [];
    }

    /**
     * 基于 3D 顶点构造 ShapeGeometry。
     * @param source 轮廓来源对象。
     * @param points 世界坐标顶点。
     * @returns 构造完成的 ShapeGeometry。
     */
    public static buildShapeGeometry(
        source: SketchCollectionObject | SketchEllipseObject | SketchCircleObject | SketchSlotObject | SketchSplineObject | SketchStyleSplineObject,
        points: THREE.Vector3[]
    ): THREE.ShapeGeometry | null {
        if (!Array.isArray(points) || points.length < 3) {
            SketchContour.logContourError('轮廓点数量不足，无法构建轮廓几何。');
            return null;
        }
        return new SketchContourGeometryUpdater(source.referencePlane).createGeometry(points);
    }
}
